/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// pin-resolve.js — turning "U1.A1" into a pin number, fail-closed.
//
// A generated netlist names pins the way a datasheet does; the model needs pin
// NUMBERS. That translation is the single most dangerous step in the pipeline,
// because a wrong-but-plausible answer produces a circuit that simulates
// happily and computes the wrong thing. Quietly resolving `S0` to `S1` because
// it looks close would invert an LSB and nothing downstream would notice. So
// every rung here either resolves exactly or reports why it could not.
//
// What the catalog actually looks like (audited, not assumed):
//
//   * Pin names are CASE-DISTINGUISHED and case-sensitively unique. 74LS47 has
//     uppercase inputs A,B,C,D and lowercase segment outputs a,b,c,d,e,f,g;
//     seg8ca has lowercase segments a..g,dp and an uppercase A common anode.
//     Matching case-insensitively would MANUFACTURE an ambiguity that the
//     catalog does not have, so exact case is tried first and usually wins.
//   * The only genuinely duplicated names are `NC` (74LS20/30/90, rom-8k,
//     ram-8k) and `VSS` (AM27C1024). Every duplicate shares one role, so the
//     duplicates are electrically interchangeable — not ambiguous, just plural.
//     That holds for EXACT names only. Once a loose rung folds case or drops
//     punctuation, distinct pins meet: `1Q` and `1Q̄` are both outputs, and so
//     are the 74LS85's `A>B`, `A=B` and `A<B`. Two DIFFERENT names are never
//     interchangeable however alike their roles, so a loose match that lands on
//     more than one name is reported, never collapsed to the lowest pin.
//   * The punctuation that matters is the punctuation that carries MEANING.
//     An overbar, a leading `/` or `~`, a trailing `'` all say "active-low" or
//     "the complement" — `1Q'`, `/1Q`, `~1Q` and `1Q̅` all mean `1Q̄`, never
//     `1Q` — and `>`, `<`, `=` name different comparator outputs. `canonical`
//     keeps both kinds and drops only what separates words (`R0(1)` → `R01`).
//   * Power pins are declared by ROLE, not by name, which is what lets 74LS83
//     (VCC=5, GND=12) resolve with no special-casing beside 74LS283 (16/8).
//
// Desk bricks (psu, clock) carry `terminals` rather than `pins`, so a member
// like `clk1.out` resolves to a terminal id instead of a pin number.

import { partDef, pinGroupsOf } from "../catalog/index.js";

/** Net members that name a supply rail rather than a part pin. */
export const RAIL_TOKENS = Object.freeze({
  VCC: "VCC",
  "+": "VCC",
  GND: "GND",
  "-": "GND",
  VSS: "GND",
  VDD: "VCC",
});

const ROLE_TOKENS = Object.freeze({
  VCC: "vcc",
  VDD: "vcc",
  GND: "gnd",
  VSS: "gnd",
});

/** Overbars, however they were typed: combining macron/overline, spacing ones. */
const OVERBAR = /[̄̅¯‾]/g;
/** A leading active-low mark: `/INT`, `~CLR`, `!OE`. */
const LEADING_LOW = /^[/~!\\]+/;
/** A trailing one: `QH'`, `CE#`, `OE_N`, `QBAR`. */
const TRAILING_LOW = /(?:['’′`*#]+|_[NL]|_?BAR)$/i;
/** Relations are part of a comparator pin's NAME: `A>B` is not `A=B`. */
const RELATIONS = Object.freeze({
  ">": "GT",
  "<": "LT",
  "=": "EQ",
  "≥": "GE",
  "≤": "LE",
  "≠": "NE",
});

/**
 * A pin name reduced to what it SAYS: its words, upper-cased and stripped of
 * separators, plus whether it is marked active-low / complemented.
 *
 * `R0(1)` → `{key: "R01"}`, `A=B` → `{key: "AEQB"}`, and `1Q̄`, `1Q'`, `/1Q`,
 * `~1Q` → `{key: "1Q", low: true}`. Applied to the token AND the catalog's
 * names, so the two meet on the same terms.
 *
 * @returns {{key: string, low: boolean}}
 */
export function canonical(name) {
  let s = String(name).normalize("NFD").trim();
  let low = false;
  if (OVERBAR.test(s)) {
    low = true;
    s = s.replace(OVERBAR, "");
  }
  if (LEADING_LOW.test(s)) {
    low = true;
    s = s.replace(LEADING_LOW, "");
  }
  if (TRAILING_LOW.test(s)) {
    low = true;
    s = s.replace(TRAILING_LOW, "");
  }
  // Letters are kept whatever their script: a CD4060B's φO and φ̄O are not
  // the bare "O" that stripping to ASCII left of them (and a 4020's lone φ
  // was the empty string).
  const key = s
    .replace(/[̀-ͯ]/g, "")
    .replace(/[><=≥≤≠]/g, (c) => RELATIONS[c])
    .replace(/[^\p{L}\p{N}]/gu, "")
    .toUpperCase();
  return { key, low };
}

const fail = (code, message, extra = {}) => ({
  ok: false,
  code,
  message,
  ...extra,
});

/**
 * Split a net member into its addressable parts.
 *
 * `"VCC"` / `"GND"` → `{ kind: "rail", rail }`
 * `"U1.A1"`         → `{ kind: "part", partId, pinToken }`
 *
 * @param {string} token
 * @returns {object|null} null when the token is not a member at all
 */
export function parseMember(token) {
  if (typeof token !== "string") return null;
  const t = token.trim();
  if (!t) return null;

  const rail = RAIL_TOKENS[t.toUpperCase()];
  if (rail && !t.includes(".")) return { kind: "rail", rail };

  const dot = t.indexOf(".");
  if (dot <= 0 || dot === t.length - 1) return null;
  return {
    kind: "part",
    partId: t.slice(0, dot),
    pinToken: t.slice(dot + 1),
  };
}

/**
 * Resolve a pin token against a catalog ref.
 *
 * @param {string} ref       catalog id, e.g. "74LS283"
 * @param {string} pinToken  "7" | "A1" | "VCC" | "Q[3]" | "out"
 * @returns {{ok:true, kind:"pin", pin:number}
 *          |{ok:true, kind:"terminal", terminal:string}
 *          |{ok:false, code:string, message:string, candidates?:Array}}
 */
export function resolvePin(ref, pinToken) {
  const def = partDef(ref);
  if (!def) return fail("UNKNOWN_REF", `No catalog part with id "${ref}".`);
  if (typeof pinToken !== "string" || !pinToken.trim()) {
    return fail("BAD_PIN_TOKEN", `Empty pin on "${ref}".`);
  }
  const token = pinToken.trim();

  // A desk brick has terminals, not pins — a different namespace entirely.
  if (def.terminals?.length) return resolveTerminal(def, ref, token);

  const pins = def.pins ?? [];
  if (!pins.length) return fail("NO_PINS", `"${ref}" has no pins to address.`);

  // ⓪ `#4` is an explicit PIN NUMBER — the escape hatch for the one place a
  //    bare integer is ambiguous (see below).
  const explicit = /^#(\d+)$/.exec(token);
  if (explicit) {
    const n = Number(explicit[1]);
    if (pins.some((p) => p.n === n)) return { ok: true, kind: "pin", pin: n };
    return fail(
      "PIN_OUT_OF_RANGE",
      `"${ref}" has no pin ${n} (it has ${pins.length}).`,
    );
  }

  // ① A bare integer usually IS the pin number — but a handful of parts name
  //    their pins numerically too. For bar8/rnet9/resistor and most switches
  //    the two readings agree, so nothing is at stake. Where they disagree —
  //    74LS148's inputs NAMED 0..7, the CD4017B/CD4022B/CD4028B's decoded
  //    outputs, the slide switch's pin 3 (named "2") — `74LS148.4` could mean
  //    pin 4 (which is named "7") or the input named "4" (which is pin 1).
  //    Guessing there would silently mis-wire the part, so both readings are
  //    reported and the caller picks with `#4` or an unambiguous name.
  if (/^\d+$/.test(token)) {
    const n = Number(token);
    const byNumber = pins.find((p) => p.n === n);
    const byName = pins.filter((p) => p.name === token);
    if (byNumber && byName.length && !byName.some((p) => p.n === n)) {
      return fail(
        "AMBIGUOUS_PIN",
        `"${ref}" reads "${token}" two ways: pin ${n} (named "${byNumber.name}") ` +
          `or the pin named "${token}" (pin ${byName[0].n}). ` +
          `Use "#${n}" for the pin number, or an unambiguous name.`,
        { candidates: [n, byName[0].n] },
      );
    }
    if (byNumber) return { ok: true, kind: "pin", pin: n };
    if (byName.length) return oneOf(byName, ref, token);
    return fail(
      "PIN_OUT_OF_RANGE",
      `"${ref}" has no pin ${n} (it has ${pins.length}).`,
    );
  }

  // ② Exact, case-sensitive. This is the rung that resolves essentially every
  //    real member, and doing it BEFORE any case folding is what keeps
  //    74LS47's `A` (input) distinct from its `a` (segment output).
  const exact = pins.filter((p) => p.name === token);
  if (exact.length) return oneOf(exact, ref, token);

  // ③ Role. Power pins are declared by role, so this is how a chip with
  //    non-standard power pins resolves without knowing which chip it is.
  const role = ROLE_TOKENS[token.toUpperCase()];
  if (role) {
    const byRole = pins.filter((p) => p.role === role);
    // A part with two DIFFERENT ground pins (a CD405x's VSS and VEE): the
    // one the token names, in any case, before falling back on the role.
    const named = byRole.filter(
      (p) => p.name.toUpperCase() === token.toUpperCase(),
    );
    if (named.length) return oneOf(named, ref, token);
    if (byRole.length) return oneOf(byRole, ref, token);
  }

  // ④ Case-insensitive. Only reached when exact case missed, so a hit here
  //    means the model got the case wrong. Fine when it is unambiguous;
  //    reported when it is not, because guessing between an input and an
  //    output is exactly the silent-wrong-circuit case.
  const loose = pins.filter(
    (p) => p.name.toLowerCase() === token.toLowerCase(),
  );
  if (loose.length) return oneName(loose, ref, token);

  // ⑤ Bus index — `Q[3]`, `A[12]` — against the def's own pinGroups, which is
  //    catalog data rather than a naming heuristic. Ahead of the punctuation
  //    rung, which would otherwise read the brackets as noise.
  const bus = /^([A-Za-z_][A-Za-z0-9_]*)\s*\[\s*(\d+)\s*\]$/.exec(token);
  if (bus) {
    const groups = pinGroupsOf(ref) ?? [];
    const group = groups.find(
      (g) => g.name.toLowerCase() === bus[1].toLowerCase(),
    );
    if (!group) {
      return fail("UNKNOWN_BUS", `"${ref}" has no bus "${bus[1]}".`, {
        candidates: groups.map((g) => g.name),
      });
    }
    const idx = Number(bus[2]);
    if (idx < 0 || idx >= group.pins.length) {
      return fail(
        "BUS_INDEX_OUT_OF_RANGE",
        `"${ref}" bus ${group.name} has ${group.pins.length} lines; ${idx} is out of range.`,
      );
    }
    return { ok: true, kind: "pin", pin: group.pins[idx] };
  }

  // ⑥ Notation. `R0(1)` written as `R01`, `A>B` as `A_GT_B`, `1Q̄` as `1Q'`,
  //    `/INT` as `~INT` — the same words and the same POLARITY, spelled
  //    differently (`canonical`). A token that says "active-low" only ever
  //    meets a pin that says so: `1Q'` is `1Q̄`, never `1Q`.
  const wanted = canonical(token);
  const spelled = pins.filter((p) => {
    const c = canonical(p.name);
    return c.key === wanted.key && c.low === wanted.low;
  });
  if (spelled.length) return oneName(spelled, ref, token);
  // A token with NO mark may still name a pin whose name carries one — `INT`
  // for the Z80's `/INT`, the way anyone says it aloud — and a marked token
  // may name an INPUT the catalog writes bare (`/CLR` for the '161's `CLR`):
  // a chip never has both, so there is only one pin it can mean. An OUTPUT is
  // different, because there the mark means the COMPLEMENT: `1Q'` on the
  // '174 asks for an inverted output the part does not have, and handing it
  // the true `1Q` is the silent wrong circuit this module exists to refuse.
  const bare = pins.filter((p) => canonical(p.name).key === wanted.key);
  const drives = (p) => p.role === "output" || p.role === "io";
  if (bare.length && (!wanted.low || !bare.some(drives))) {
    return oneName(bare, ref, token);
  }
  if (bare.length) {
    return fail(
      "UNKNOWN_PIN",
      `"${ref}" has no active-low or complement pin "${token}" — ` +
        `${bare.map((p) => `"${p.name}" (pin ${p.n})`).join(", ")} carries ` +
        `no such mark. Name the pin exactly as the catalog lists it.`,
      { candidates: bare.map((p) => p.name) },
    );
  }

  // ⑦ Give up — but say what was available, so a repair turn has something
  //    to work with rather than a bare rejection.
  return fail("UNKNOWN_PIN", `"${ref}" has no pin named "${token}".`, {
    candidates: pins.map((p) => p.name),
  });
}

/**
 * Collapse a set of matched pins to one answer.
 *
 * Several pins CAN share a name — `NC` and `VSS` do — but only when they share
 * a role, which makes them interchangeable rather than ambiguous: two grounds
 * are the same net, and nobody wires to a particular NC. Differing roles is the
 * real ambiguity, and it is reported rather than guessed.
 */
function oneOf(matches, ref, token) {
  if (matches.length === 1) return { ok: true, kind: "pin", pin: matches[0].n };
  const roles = new Set(matches.map((p) => p.role));
  if (roles.size === 1) {
    // Deterministic: lowest pin number, so a rebuild never shifts.
    return {
      ok: true,
      kind: "pin",
      pin: Math.min(...matches.map((p) => p.n)),
    };
  }
  return fail(
    "AMBIGUOUS_PIN",
    `"${ref}" has ${matches.length} pins named "${token}" with different roles ` +
      `(${matches.map((p) => `${p.n}:${p.role}`).join(", ")}). Use the pin number.`,
    { candidates: matches.map((p) => p.n) },
  );
}

/**
 * `oneOf` for a LOOSE match: collapse only when every match carries the same
 * name. A loose rung that lands on two different names — `1Q` and `1Q̄`, `A>B`
 * and `A<B` — has found two different pins, and however alike their roles, the
 * lowest-numbered one is not an answer.
 */
function oneName(matches, ref, token) {
  const names = [...new Set(matches.map((p) => p.name))];
  if (names.length === 1) return oneOf(matches, ref, token);
  return fail(
    "AMBIGUOUS_PIN",
    `"${ref}" has no pin named exactly "${token}", and it could mean any of ` +
      `${matches.map((p) => `"${p.name}" (pin ${p.n})`).join(", ")}. ` +
      `Use the exact name or "#" and the pin number.`,
    { candidates: matches.map((p) => p.n) },
  );
}

/**
 * Brick terminals (`psu1.+`, `clk1.out`) — a small, exact namespace.
 *
 * The numeric branch is the generic escape hatch for a part declaring BOTH
 * pins and terminals: a brick is addressed by TERMINAL, but a datasheet pin
 * NUMBER should still get you there. No shipped def is that shape today (the
 * HD44780 LCD was, until it became two board-seated modules whose pins are
 * holes), so the branch is unexercised by the catalog — kept because the cost
 * is three lines and the alternative is a confusing failure for the next brick
 * that grows a datasheet pin table.
 */
function resolveTerminal(def, ref, token) {
  const exact = def.terminals.find((t) => t.id === token);
  if (exact) return { ok: true, kind: "terminal", terminal: exact.id };
  const loose = def.terminals.find(
    (t) => t.id.toLowerCase() === token.toLowerCase(),
  );
  if (loose) return { ok: true, kind: "terminal", terminal: loose.id };

  const numeric = /^#?(\d+)$/.exec(token);
  if (numeric && def.pins?.length) {
    const named = def.pins.find((p) => p.n === Number(numeric[1]));
    const viaPin = named && def.terminals.find((t) => t.id === named.name);
    if (viaPin) return { ok: true, kind: "terminal", terminal: viaPin.id };
  }

  return fail("UNKNOWN_TERMINAL", `"${ref}" has no terminal "${token}".`, {
    candidates: def.terminals.map((t) => t.id),
  });
}
